import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigService } from './config-service';

/** Fields that must never be trusted from the client; the backend
 *  resolves ownership via the JWT carried by the interceptor. */
type OwnershipFields = 'customerID' | 'customerName' | 'applicationId';

export interface RequiredDocumentPayload {
    id: number;
    folderDocumentID: number;
    documentID: number;
    documentName: string;
    collateral: string;
    fileName: string;
    fileData: any;
    isAdditionalDocuments: boolean;
    isDeleted: boolean;
    isNew: boolean;
    customerID?: string; // included for informational/logging purposes only; backend ignores it for ownership
    customerName?: string; // included for informational/logging purposes only; backend ignores it for ownership
    applicationId?: string; // included for informational/logging purposes only; backend ignores it for ownership
}

@Injectable({
    providedIn: 'root'
})
export class VerifyEmailService {
    /**
     * Getter ensures apiBaseUrl is read after APP_INITIALIZER has finished
     * loading lmsConfig.json — not at construction time.
     */
    private get baseUrl(): string {
        return this.configService.get('apiBaseUrl') || '';
    }

    constructor(private httpClient: HttpClient, private configService: ConfigService) {}

    /**
     * Strips ownership fields (customerID, customerName, applicationId) from
     * each document before sending to the backend. The backend must derive
     * ownership from the JWT rather than trusting client-supplied values.
     */
    private sanitizeDocuments(docs: any[]): RequiredDocumentPayload[] {
        return docs.map(doc => {
            const sanitized: RequiredDocumentPayload = {
                id: doc.id,
                folderDocumentID: doc.folderDocumentID,
                documentID: doc.documentID,
                documentName: doc.documentName,
                collateral: doc.collateral,
                fileName: doc.fileName,
                fileData: doc.fileData,
                isAdditionalDocuments: doc.isAdditionalDocuments,
                isDeleted: doc.isDeleted,
                isNew: doc.isNew,
                customerID: doc.customerID, // included for informational/logging purposes only; backend ignores it for ownership
                customerName: doc.customerName, // included for informational/logging purposes only; backend ignores it for ownership
                applicationId: doc.applicationId // included for informational/logging purposes only; backend ignores it for ownership
            };
            return sanitized;
        });
    }

    public sendVerificationEmail(verificationEmailId: any): Observable<any> {
        return this.httpClient
            .post(this.baseUrl + 'VerifyPartyEmail?EmailVerificationID=' + encodeURIComponent(verificationEmailId), null)
            .pipe(catchError(this.ErrorHandler));
    }

    public passwordValidationLink(passwordLinkGuid: any): Observable<any> {
        return this.httpClient
            .post(this.baseUrl + 'PasswordValidationLink?passwordLinkGuid=' + encodeURIComponent(passwordLinkGuid), null)
            .pipe(catchError(this.ErrorHandler));
    }

    /**
     * Sends documents to the backend.
     * Ownership (customerID, applicationId) is intentionally omitted from the
     * payload; the backend resolves it from the Bearer JWT in the request header.
     */
    public postRequiredDocuments(docs: any[]): Observable<any> {        
        const payload = this.sanitizeDocuments(docs);
        // const key = btoa('EncodedLMSKeyForRequiredDocuments');
        // const headers = { Authorization: `Bearer ${key}` };
        // return this.httpClient
        //     .post(this.baseUrl + 'PostRequiredDocuments', payload, { headers })
        //     .pipe(catchError(this.ErrorHandler));

        return this.httpClient
            .post(this.baseUrl + 'PostRequiredDocuments', payload)
            .pipe(catchError(this.ErrorHandler));
    }

    public getRequiredDocuments(passwordLinkGuid: any): Observable<any> {
        return this.httpClient
            .post(this.baseUrl + 'GetRequiredDocuments?passwordLinkGuid=' + encodeURIComponent(passwordLinkGuid), null)
            .pipe(catchError(this.ErrorHandler));
    }

    public IsACKConsentUpdation(verificationEmailId: any): Observable<any> {
        return this.httpClient
            .post(this.baseUrl + 'IsACKConsentUpdation?GUID=' + encodeURIComponent(verificationEmailId), null)
            .pipe(catchError(this.ErrorHandler));
    }

    public IsACKConsentNoThankUpdation(verificationEmailId: any): Observable<any> {
        return this.httpClient
            .post(this.baseUrl + 'ConsentNoThanksUpdation?GUID=' + encodeURIComponent(verificationEmailId), null)
            .pipe(catchError(this.ErrorHandler));
    }

    ErrorHandler(error: any): Observable<never> {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
            errorMessage = `Error: ${error.error.message}`;
        } else {
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        return throwError(errorMessage);
    }
}